import React, { useRef, useState, useEffect, useImperativeHandle, useCallback } from 'react';
import TextField from '@material-ui/core/TextField';

import AuthApi from '../../Services/Authapi';
import './TaskManagement.css';

// Helper to check if HTML content is visually empty
export const isWysiwygEmpty = (html) => {
  if (!html) return true;
  if (typeof html !== 'string') return false;
  if (/<(img|video|audio|iframe|table|hr)\b/i.test(html)) return false;
  if (/class=["'][^"']*tm-desc-file-card/i.test(html)) return false;
  const clean = html
    .replace(/<[^>]+>/g, '')
    .replace(/&(nbsp|amp|quot|lt|gt);/g, ' ')
    .replace(/&#\d+;/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
  return clean.length === 0;
};

// Convert plain text or mixed bullet text into structured HTML <ul><li> with hanging indent support
export const textToWysiwygHtml = (text) => {
  if (!text || typeof text !== 'string') return '';

  let normalized = text;
  if (
    /<(div|p|br)\b/i.test(text) &&
    !/<\/?(ul|ol|li)\b/i.test(text) &&
    !/<img\b/i.test(text) &&
    !/tm-desc-file-card/i.test(text)
  ) {
    normalized = text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<p[^>]*>/gi, '');
  }

  const lines = normalized.split(/\r?\n/);
  let html = '';
  let inList = false;

  const sanitizeLineContent = (str) => {
    if (!str) return '';
    if (/<(b|strong|i|em|u|span|a|img|div|table|svg)\b/i.test(str)) {
      return str;
    }
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const bulletMatch = rawLine.match(/^(\s*)([•◦▪*-]|\d+\.)\s+(.*)$/);

    if (bulletMatch) {
      const leadingSpaces = bulletMatch[1] || '';
      const bulletChar = bulletMatch[2];
      const content = (bulletMatch[3] || '').trim();

      let level = 1;
      const spaceCount = leadingSpaces.length;

      if (spaceCount >= 12) {
        level = 4;
      } else if (bulletChar === '•') {
        level = spaceCount >= 8 ? 3 : spaceCount >= 4 ? 2 : 1;
      } else if (bulletChar === '◦') {
        level = spaceCount >= 8 ? 4 : 2;
      } else if (bulletChar === '▪') {
        level = spaceCount >= 8 ? 4 : 3;
      } else {
        // '-', '*', '.'
        level = Math.min(Math.floor(spaceCount / 4) + 1, 6);
      }

      if (!inList) {
        html += '<ul>';
        inList = true;
      }

      html += `<li data-level="${level}" class="tm-bullet-l${level}">${sanitizeLineContent(content)}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }

      if (!rawLine.trim()) {
        html += '<div><br></div>';
      } else {
        html += `<div>${sanitizeLineContent(rawLine)}</div>`;
      }
    }
  }

  if (inList) {
    html += '</ul>';
  }

  return html;
};

// Sanitize bullets: converts any raw bullet lines into real HTML <ul><li> with native hanging indent & levels
export const sanitizeBulletHtml = (input) => {
  if (!input || typeof input !== 'string') return '';

  let str = input;

  // Clean up any accidentally dragged/pasted drawer UI DOM elements
  if (/tm-drawer-section|tm-subtask-inline-add|MuiFormControl-root/i.test(str)) {
    str = str.replace(/<div[^>]*class=["'][^"']*(?:tm-subtask-inline-add|MuiFormControl-root)[^"']*["'][^>]*>[\s\S]*?<\/div>/gi, '');
    str = str.replace(/<div[^>]*class=["'][^"']*tm-drawer-section[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi, '$1');
  }

  // Strip text-align: center from any inline styles so text is always aligned left
  str = str.replace(/text-align:\s*(?:center|right|justify);?/gi, '');

  // Strip inline font-style: italic, font-size, and foreign colors from div/span/p so font matches Task Name
  str = str.replace(/font-style:\s*italic;?/gi, '');
  str = str.replace(/font-size:\s*[^;"]+;?/gi, '');
  str = str.replace(/color:\s*rgb\([^)]+\);?/gi, '');
  str = str.replace(/\s*style=["']\s*["']/gi, '');

  if (/<\/?(ul|ol|li)\b/i.test(str)) {
    return str.replace(/<li([^>]*)>(\s*(?:[•◦▪*-]|\d+\.)\s*)?/gi, (match, attrs, bullet) => {
      let level = '1';
      const levelMatch = attrs.match(/data-level=["']?(\d+)["']?/i) || attrs.match(/tm-bullet-l(\d+)/i);
      if (levelMatch) {
        level = levelMatch[1];
      } else if (bullet && bullet.includes('▪')) {
        level = '3';
      } else if (bullet && bullet.includes('◦')) {
        level = '2';
      }
      return `<li data-level="${level}" class="tm-bullet-l${level}">`;
    });
  }
  if ((/[•◦▪]|\n\s*[-*]\s+/.test(str) || /^\s*[-*]\s+/.test(str)) && !/<(img|a|div|span)\b/i.test(str)) {
    return textToWysiwygHtml(str);
  }
  return str;
};

// Backward compatibility alias
export const sanitizeBulletText = (text) => sanitizeBulletHtml(text);

export const getBulletLevel = (spaces, bulletChar) => {
  const spaceCount = (spaces || '').length;
  if (bulletChar === '•') return 1;
  if (bulletChar === '◦') return 2;
  if (bulletChar === '▪') return Math.max(3, Math.floor(spaceCount / 4));
  return Math.max(1, Math.floor(spaceCount / 4));
};

export const getPrefixForLevel = (level) => {
  if (level <= 0) return '';
  if (level === 1) return '• ';
  if (level === 2) return '    ◦ ';
  const indentCount = (level - 1) * 4;
  return ' '.repeat(indentCount) + '▪ ';
};

// Helper to format file size cleanly
const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const num = Number(bytes);
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(0)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper for file type icons
const getFileIcon = (fileName, mimeType) => {
  const lower = (fileName || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();
  if (lower.endsWith('.pdf') || mime === 'application/pdf') return '📕';
  if (lower.endsWith('.doc') || lower.endsWith('.docx') || mime.includes('word')) return '📘';
  if (lower.endsWith('.xls') || lower.endsWith('.xlsx') || lower.endsWith('.csv') || mime.includes('excel') || mime.includes('spreadsheet')) return '📗';
  if (lower.endsWith('.zip') || lower.endsWith('.rar') || lower.endsWith('.7z') || mime.includes('zip') || mime.includes('compressed')) return '🗂️';
  if (lower.endsWith('.txt') || mime.includes('text')) return '📝';
  return '📁';
};

// Helper to convert File to base64 Data URL for instant user display
const readFileAsDataURL = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
};

// Custom contentEditable input component passed to Material-UI TextField via InputProps.inputComponent
const WysiwygContentInput = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    className,
    style,
    disabled,
    readOnly,
    rows,
    autoFocus,
    onFileUpload,
    ...other
  } = props;

  const contentRef = useRef(null);
  const isTypingRef = useRef(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const checkIsEmpty = (html) => isWysiwygEmpty(html);
  const [isEmpty, setIsEmpty] = useState(checkIsEmpty(value));

  // Sync external value with editor content when not actively typing
  useEffect(() => {
    setIsEmpty(checkIsEmpty(value));
    if (disabled || readOnly) return;
    if (contentRef.current && !isTypingRef.current) {
      const currentHtml = contentRef.current.innerHTML;
      const targetVal = sanitizeBulletHtml(value || '');
      if (currentHtml !== targetVal) {
        contentRef.current.innerHTML = targetVal;
      }
    }
  }, [value, disabled, readOnly]);

  const handleInput = useCallback(() => {
    if (disabled || readOnly || !contentRef.current) return;
    isTypingRef.current = true;
    const html = contentRef.current.innerHTML;
    const textEmpty = checkIsEmpty(html);
    setIsEmpty(textEmpty);

    if (onChange) {
      onChange({
        target: {
          value: textEmpty ? '' : html,
          name: props.name || 'description',
        },
      });
    }

    setTimeout(() => {
      isTypingRef.current = false;
    }, 0);
  }, [onChange, props.name, disabled, readOnly]);

  // Insert HTML safely at the current caret position or at the end
  const insertHtmlAtCaret = useCallback((html) => {
    if (!contentRef.current) return;
    contentRef.current.focus();
    if (isWysiwygEmpty(contentRef.current.innerHTML)) {
      contentRef.current.innerHTML = '';
    }
    const sel = window.getSelection();
    if (sel && sel.rangeCount && contentRef.current.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const el = document.createElement('div');
      el.innerHTML = html;
      const frag = document.createDocumentFragment();
      let node;
      let lastNode;
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else {
      const el = document.createElement('div');
      el.innerHTML = html;
      while (el.firstChild) {
        contentRef.current.appendChild(el.firstChild);
      }
    }
  }, []);

  // Universal handler for inserting & uploading images or document files (Screenshots, PDFs, etc.)
  const handleInsertAndUploadFile = useCallback(async (file) => {
    if (!file || disabled || readOnly) return;

    const isImage = file.type
      ? file.type.startsWith('image/')
      : /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(file.name || '');

    const tempId = 'media_' + Date.now() + '_' + Math.random().toString(36).substr(2, 7);

    

    if (isImage) {
      // 1. Instant base64 preview so user sees screenshot / pasted photo immediately
      const base64 = await readFileAsDataURL(file);
      const safeName = file.name && file.name !== 'image.png' ? file.name : `screenshot-${Date.now()}.png`;

      const tempHtml = `<div class="tm-desc-img-wrap" data-temp-id="${tempId}" style="margin: 8px 0; max-width: 100%;"><img src="${base64}" alt="${safeName}" class="tm-pasted-desc-image" style="max-width: 100%; max-height: 420px; height: auto; border-radius: 6px; border: 1px solid #cbd5e1; display: block; margin: 8px 0; box-shadow: 0 1px 4px rgba(0,0,0,0.12); cursor: pointer;" title="Click to view full image" /></div><div><br></div>`;
      insertHtmlAtCaret(tempHtml);
      handleInput();

      // 2. Server upload in background to get permanent URL
      try {
        const uploadFileObj = (file instanceof File) ? file : new File([file], safeName, { type: file.type || 'image/png' });
        const res = await AuthApi.uploadTaskFile(uploadFileObj);
        if (res && res.status && res.data) {
          const publicUrl = res.data.url || res.data.file_url;
          if (publicUrl && contentRef.current) {
            const containerEl = contentRef.current.querySelector(`[data-temp-id="${tempId}"]`);
            if (containerEl) {
              const imgEl = containerEl.querySelector('img');
              if (imgEl) {
                imgEl.src = publicUrl;
              }
              containerEl.removeAttribute('data-temp-id');
            }
            handleInput();
          }
          if (typeof onFileUpload === 'function') {
            onFileUpload(res.data);
          }
        }
      } catch (err) {
        console.error('Failed to upload pasted image to server', err);
      }
    } else {
      // 1. Document / PDF file: Insert interactive file card
      const fileIcon = getFileIcon(file.name, file.type);
      const loadingHtml = `<div class="tm-desc-file-card" data-temp-id="${tempId}" contenteditable="false" style="display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; margin: 6px 0; background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px; font-family: Roboto, sans-serif; font-size: 13px; user-select: none;"><span>${fileIcon}</span><span style="color: #64748b; font-weight: 500;">Uploading ${file.name}...</span></div><div><br></div>`;
      insertHtmlAtCaret(loadingHtml);
      handleInput();

      // 2. Server upload in background
      try {
        const res = await AuthApi.uploadTaskFile(file);
        if (res && res.status && res.data) {
          const publicUrl = res.data.url || res.data.file_url || '#';
          const formattedSize = formatFileSize(file.size);
          if (contentRef.current) {
            const cardEl = contentRef.current.querySelector(`.tm-desc-file-card[data-temp-id="${tempId}"]`);
            if (cardEl) {
              cardEl.removeAttribute('data-temp-id');
              cardEl.innerHTML = `<span>${fileIcon}</span><a href="${publicUrl}" target="_blank" rel="noopener noreferrer" style="color: #036dda; font-weight: 600; text-decoration: underline;" download="${file.name}">${file.name}</a><span class="tm-file-size" style="color: #64748b; font-size: 11px;">(${formattedSize})</span>`;
            }
            handleInput();
          }
          if (typeof onFileUpload === 'function') {
            onFileUpload(res.data);
          }
        }
      } catch (err) {
        console.error('Failed to upload PDF/file to server', err);
        if (contentRef.current) {
          const cardEl = contentRef.current.querySelector(`.tm-desc-file-card[data-temp-id="${tempId}"]`);
          if (cardEl) {
            cardEl.innerHTML = `<span>⚠️</span><span style="color: #ef4444; font-size: 12px;">Upload failed for ${file.name}</span>`;
          }
          handleInput();
        }
      }
    }
  }, [disabled, readOnly, insertHtmlAtCaret, handleInput, onFileUpload]);

  // Expose focus, value, and insertFile method to Material-UI InputBase & parents
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (contentRef.current) {
        contentRef.current.focus();
      }
    },
    value: value || '',
    scrollWidth: contentRef.current ? contentRef.current.scrollWidth : 0,
    scrollHeight: contentRef.current ? contentRef.current.scrollHeight : 0,
    insertFile: handleInsertAndUploadFile,
  }));

  // Paste handler: Automatically handles Screenshots, Images, PDFs, and Files along with rich text
  const handlePaste = async (e) => {
    if (disabled || readOnly) return;
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) return;

    // Check for files or pasted screenshots on clipboard
    const items = Array.from(clipboardData.items || []);
    const files = Array.from(clipboardData.files || []);

    const filesToProcess = [];
    if (items.length > 0) {
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) filesToProcess.push(file);
        }
      }
    }
    if (filesToProcess.length === 0 && files.length > 0) {
      filesToProcess.push(...files);
    }

    if (filesToProcess.length > 0) {
      e.preventDefault();
      for (const file of filesToProcess) {
        await handleInsertAndUploadFile(file);
      }
      return;
    }

    // Standard text paste with list/indent support
    e.preventDefault();
    const text = clipboardData.getData('text/plain') || '';
    if (text) {
      const html = textToWysiwygHtml(text);
      document.execCommand('insertHTML', false, html);
      handleInput();
    }
  };

  // Drag and Drop handlers for PDFs, Images, and Documents
  const handleDragEnter = (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const dt = e.dataTransfer;
    if (!dt) return;
    const files = Array.from(dt.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      await handleInsertAndUploadFile(file);
    }
  };

  // Click on pasted image in description opens it in a new window/tab for full resolution inspection
  const handleClick = (e) => {
    const target = e.target;
    if (target && target.tagName === 'IMG' && target.src) {
      e.stopPropagation();
      window.open(target.src, '_blank');
    }
  };

  const getClosestLi = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !contentRef.current) return null;
    let node = sel.getRangeAt(0).startContainer;
    while (node && node !== contentRef.current) {
      if (node.nodeName === 'LI') return node;
      node = node.parentNode;
    }
    return null;
  };

  const handleKeyDown = (e) => {
    if (disabled || readOnly) return;
    // Rich Text Formatting Shortcuts: Ctrl+B, Ctrl+I, Ctrl+U
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        document.execCommand('bold', false, null);
        handleInput();
        return;
      }
      if (key === 'i') {
        e.preventDefault();
        document.execCommand('italic', false, null);
        handleInput();
        return;
      }
      if (key === 'u') {
        e.preventDefault();
        document.execCommand('underline', false, null);
        handleInput();
        return;
      }
    }

    // 1. Space key: convert "- ", "* ", ". ", or "• " at line start into real HTML list with hanging indent
    if (e.key === ' ' || e.code === 'Space') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && contentRef.current) {
        const range = sel.getRangeAt(0);
        if (range.collapsed && typeof sel.modify === 'function') {
          const cloneRange = range.cloneRange();
          sel.removeAllRanges();
          sel.addRange(cloneRange);
          sel.modify('extend', 'backward', 'lineboundary');
          const textBefore = sel.toString();

          const match = textBefore.match(/^(\s*)([-*•◦▪]|\.)\s*$/);
          if (match && ['-', '*', '.', '•', '◦', '▪'].includes(match[2])) {
            e.preventDefault();
            const spaceCount = match[1].length;
            let level = 1;
            if (spaceCount >= 12) {
              level = 4;
            } else if (match[2] === '▪' || spaceCount >= 8) {
              level = 3;
            } else if (match[2] === '◦' || spaceCount >= 4) {
              level = 2;
            }

            document.execCommand('delete');
            const currentLi = getClosestLi();
            if (!currentLi) {
              document.execCommand('insertUnorderedList', false, null);
            }
            const li = getClosestLi();
            if (li) {
              li.setAttribute('data-level', String(level));
              li.className = 'tm-bullet-l' + level;
            }
            handleInput();
            return;
          } else {
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
      return;
    }

    // 2. Tab key: Indent to sub-point (Level 2: ◦, Level 3: ▪) or outdent
    if (e.key === 'Tab') {
      e.preventDefault();
      const li = getClosestLi();
      if (li) {
        const currentLevel = parseInt(li.getAttribute('data-level') || '1', 10);
        if (e.shiftKey) {
          // Outdent
          if (currentLevel > 1) {
            const newLevel = currentLevel - 1;
            li.setAttribute('data-level', String(newLevel));
            li.className = 'tm-bullet-l' + newLevel;
            handleInput();
            return;
          } else {
            document.execCommand('outdent', false, null);
            handleInput();
            return;
          }
        } else {
          // Indent to sub-point
          const newLevel = Math.min(currentLevel + 1, 6);
          li.setAttribute('data-level', String(newLevel));
          li.className = 'tm-bullet-l' + newLevel;
          handleInput();
          return;
        }
      }

      // Check if user typed "-" or "*" or "." right before cursor
      const sel = window.getSelection();
      if (sel && sel.rangeCount && !e.shiftKey && typeof sel.modify === 'function') {
        const range = sel.getRangeAt(0);
        const cloneBefore = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneBefore);
        sel.modify('extend', 'backward', 'lineboundary');
        const textBefore = sel.toString();

        const match = textBefore.match(/^(\s*)([-*•◦▪]|\.)\s*$/);
        if (match && ['-', '*', '.', '•', '◦', '▪'].includes(match[2])) {
          document.execCommand('delete');
          document.execCommand('insertUnorderedList', false, null);
          const newLi = getClosestLi();
          if (newLi) {
            newLi.setAttribute('data-level', '2');
            newLi.className = 'tm-bullet-l2';
          }
          handleInput();
          return;
        } else {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }

      if (!e.shiftKey) {
        document.execCommand('insertText', false, '    ');
        handleInput();
        return;
      }
      return;
    }

    // 3. Enter key: continue list at the SAME level or cleanly exit on empty bullet
    if (e.key === 'Enter') {
      const li = getClosestLi();
      if (li) {
        const textContent = (li.textContent || '').replace(/[\s\u200B-\u200D\uFEFF]/g, '');
        const currentLevel = parseInt(li.getAttribute('data-level') || '1', 10);

        if (textContent.length === 0) {
          // Empty bullet line
          e.preventDefault();
          if (currentLevel > 1) {
            const newLevel = currentLevel - 1;
            li.setAttribute('data-level', String(newLevel));
            li.className = 'tm-bullet-l' + newLevel;
          } else {
            document.execCommand('outdent', false, null);
          }
          handleInput();
          return;
        }

        // Line has text: split item at cursor and create next <li> at the SAME level
        e.preventDefault();
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
          const range = sel.getRangeAt(0);

          const afterRange = range.cloneRange();
          afterRange.setEndAfter(li.lastChild || li);
          const afterContent = afterRange.extractContents();

          const newLi = document.createElement('li');
          newLi.setAttribute('data-level', String(currentLevel));
          newLi.className = 'tm-bullet-l' + currentLevel;

          const hasText = afterContent.textContent && afterContent.textContent.replace(/[\s\u200B-\u200D\uFEFF]/g, '').length > 0;
          const hasMedia = afterContent.querySelector && Boolean(afterContent.querySelector('img, br, iframe, video'));

          if (!hasText && !hasMedia) {
            newLi.innerHTML = '<br>';
          } else {
            if (afterContent.firstChild && afterContent.firstChild.nodeType === Node.TEXT_NODE) {
              afterContent.firstChild.textContent = afterContent.firstChild.textContent.replace(/^\s+/, '');
            }
            newLi.appendChild(afterContent);
          }

          const liHasText = li.textContent && li.textContent.replace(/[\s\u200B-\u200D\uFEFF]/g, '').length > 0;
          const liHasMedia = li.querySelector && Boolean(li.querySelector('img, br, iframe, video'));
          if (!liHasText && !liHasMedia) {
            li.innerHTML = '<br>';
          }

          if (li.nextSibling) {
            li.parentNode.insertBefore(newLi, li.nextSibling);
          } else {
            li.parentNode.appendChild(newLi);
          }

          const newRange = document.createRange();
          if (newLi.firstChild) {
            newRange.setStart(newLi.firstChild, 0);
          } else {
            newRange.setStart(newLi, 0);
          }
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
          handleInput();

          setTimeout(() => {
            if (newLi) {
              if (typeof newLi.scrollIntoView === 'function') {
                newLi.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
              }
              if (contentRef.current) {
                const parent = contentRef.current;
                const liBottom = newLi.offsetTop + (newLi.offsetHeight || 24);
                if (liBottom > parent.scrollTop + parent.clientHeight - 10) {
                  parent.scrollTop = liBottom - parent.clientHeight + 28;
                }
              }
            }
          }, 0);

          return;
        }
      } else {
        setTimeout(() => {
          if (contentRef.current) {
            const parent = contentRef.current;
            const sel = window.getSelection();
            if (sel && sel.rangeCount) {
              const range = sel.getRangeAt(0);
              const rect = range.getBoundingClientRect();
              const parentRect = parent.getBoundingClientRect();
              if (rect.bottom > parentRect.bottom - 10) {
                parent.scrollTop += (rect.bottom - parentRect.bottom) + 28;
              }
            }
          }
        }, 10);
      }
    }

    // 4. Backspace key: remove bullet cleanly on empty bullet line
    if (e.key === 'Backspace') {
      const li = getClosestLi();
      if (li) {
        const textContent = (li.textContent || '').replace(/[\s\u200B-\u200D\uFEFF]/g, '');
        const currentLevel = parseInt(li.getAttribute('data-level') || '1', 10);
        const sel = window.getSelection();
        const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;

        if (range && range.collapsed && (textContent.length === 0 || range.startOffset === 0)) {
          e.preventDefault();
          if (currentLevel > 1) {
            const newLevel = currentLevel - 1;
            li.setAttribute('data-level', String(newLevel));
            li.className = 'tm-bullet-l' + newLevel;
          } else {
            if (textContent.length === 0) {
              const parent = li.parentNode;
              li.remove();
              if (parent && !parent.hasChildNodes()) {
                parent.remove();
              }
            } else {
              document.execCommand('outdent', false, null);
            }
          }
          handleInput();
          return;
        }
      }
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div
      ref={contentRef}
      id={props.id}
      className={`tm-wysiwyg-content ${isDragOver ? 'tm-wysiwyg-drag-over' : ''} ${className || ''}`}
      contentEditable={!disabled && !readOnly}
      suppressContentEditableWarning
      data-empty={isEmpty}
      data-placeholder={placeholder || ''}
      dangerouslySetInnerHTML={
        (disabled || readOnly)
          ? { __html: sanitizeBulletHtml(value || '') }
          : undefined
      }
      onFocus={(e) => {
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        if (disabled || readOnly) return;
        if (contentRef.current) {
          const raw = contentRef.current.innerHTML;
          const sanitized = sanitizeBulletHtml(raw);
          if (sanitized !== raw) {
            contentRef.current.innerHTML = sanitized;
          }
        }
        handleInput();
        if (onBlur) onBlur(e);
      }}
      onPaste={handlePaste}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        minHeight: 110,
        maxHeight: 400,
        outline: 'none',
        width: '100%',
        textAlign: 'left',
        direction: 'ltr',
        cursor: (disabled || readOnly) ? 'default' : 'text',
        ...style,
      }}
      {...other}
    />
  );
});

// Full Material-UI TextField with WysiwygContentInput, automatic screenshot paste & file drag-and-drop
const WysiwygDescriptionEditor = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  onFileUpload,
  label = 'Description',
  placeholder = 'Enter description...',
  error = false,
  helperText = '',
  id = 'description',
  name = 'description',
  rows = 4,
  minHeight,
  className = '',
  InputLabelProps,
  InputProps,
  inputProps,
  disabled = false,
  readOnly = false,
  ...otherProps
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const editorInputRef = useRef(null);

  const hasContent = !isWysiwygEmpty(value);
  const isShrunk = (InputLabelProps && typeof InputLabelProps.shrink !== 'undefined')
    ? InputLabelProps.shrink
    : Boolean(isFocused || hasContent);

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  // Container drag & drop handlers so dragging onto any part of the field triggers upload
  const handleContainerDragEnter = (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleContainerDragOver = (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleContainerDragLeave = (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleContainerDrop = async (e) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const dt = e.dataTransfer;
    if (!dt) return;
    const droppedFiles = Array.from(dt.files || []);
    if (droppedFiles.length === 0 && dt.items) {
      for (const item of Array.from(dt.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) droppedFiles.push(f);
        }
      }
    }
    if (droppedFiles.length === 0) return;

    if (editorInputRef.current && typeof editorInputRef.current.insertFile === 'function') {
      for (const file of droppedFiles) {
        await editorInputRef.current.insertFile(file);
      }
    }
  };

  return (
    <div
      className={`tm-wysiwyg-container ${className || ''} ${isDragOver ? 'tm-wysiwyg-drag-over' : ''}`}
      style={{ width: '100%', position: 'relative' }}
      onDragEnter={handleContainerDragEnter}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDrop}
    >
      <TextField
        fullWidth
        multiline
        rows={rows}
        variant="outlined"
        id={id}
        name={name}
        label={label}
        placeholder={placeholder}
        value={value || ''}
        disabled={disabled}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={Boolean(error)}
        helperText={helperText}
        className={`tm-wysiwyg-mui-field ${error ? 'input-error' : ''}`}
        InputLabelProps={{
          shrink: isShrunk,
          ...InputLabelProps,
        }}
        InputProps={{
          inputComponent: WysiwygContentInput,
          readOnly: readOnly,
          onFocus: handleFocus,
          onBlur: handleBlur,
          className: isDragOver ? 'tm-wysiwyg-drag-over' : '',
          style: minHeight ? { minHeight, ...(InputProps && InputProps.style) } : (InputProps && InputProps.style),
          ...InputProps,
        }}
        inputProps={{
          placeholder: placeholder,
          id: id,
          name: name,
          disabled: disabled,
          readOnly: readOnly,
          onFocus: handleFocus,
          onBlur: handleBlur,
          onFileUpload: onFileUpload,
          style: minHeight ? { minHeight: minHeight - 20, ...(inputProps && inputProps.style) } : (inputProps && inputProps.style),
          ...inputProps,
        }}
        inputRef={editorInputRef}
        {...otherProps}
      />
    </div>
  );
};

export default WysiwygDescriptionEditor;
